
import {Component, Vue, Watch} from 'vue-property-decorator';

// Import to force instantiation of Stores
import 'src/store/ShortcutsStore';
import {BackEndService} from './utils/BackEndService';
import {userService} from './modules/user/UserService';
import PlayerLoader from './modules/player/components/player-view/PlayerLoader.vue';
import BuySongModal from 'src/modules/catalog/modals/BuySongModal.vue';
import InAppSurvey from './modules/global/components/InAppSurvey.vue';

import 'intro.js/introjs.css';
import {InAppSurveyConfiguration} from './modules/global/types/InAppSurveyConfiguration';
import {isInIOSApp, isInMobileApp, isInOldCordovaApp} from 'src/utils/MobileUtils';
import {analyticsController} from 'src/modules/user/analytics/AnalyticsController';
import CatalogEntryPopup from 'src/modules/catalog/components/cards/CatalogEntryPopup.vue';
import {timeTrackingController} from 'src/modules/user/activity-score/time-tracking/TimeTrackingController';
import TeacherChatbot from './modules/teacher/TeacherChatbot.vue';
import {onboardingChecklistController} from 'src/modules/onboarding/checklist/OnboardingChecklistController';
import PlugKeyboardSlide from 'src/modules/onboarding/slides/content/PlugKeyboardSlide.vue';
import StandaloneOnboardingSlide from 'src/modules/onboarding/slides/StandaloneOnboardingSlide.vue';
import {midiDispatcher} from './modules/midi/MidiDispatcher';
import WaitingSubscriptionModal from 'src/modules/user/subscription/WaitingSubscriptionModal.vue';
import {playerModals} from './modules/player/models/PlayerModals';
import PromotionPopup from './modules/global/marketing/PromotionPopup.vue';
import {synthController} from './modules/synth/SynthController';
import audioPlayer from './modules/synth/AudioPlayer';
import NotificationPermissionDeniedModal from './modules/global/components/NotificationPermissionDeniedModal.vue';
import {isLandscapePage, isOutOfAppAuthorizedPage} from 'src/utils/PageSettingsUtils';
import FreemiumSubscriptionPopup from './modules/global/components/FreemiumSubscriptionPopup.vue';

import * as Tone from 'tone';

@Component({
  components: {
  WaitingSubscriptionModal,
  StandaloneOnboardingSlide,
  PlugKeyboardSlide,
  TeacherChatbot,
  CatalogEntryPopup,
  InAppSurvey,
  PlayerLoader,
  BuySongModal,
  PromotionPopup,
  NotificationPermissionDeniedModal,
  FreemiumSubscriptionPopup
  },
  })
export default class App extends Vue {
  private loaded: boolean = false;
  private inAppSurveyConfigurations: InAppSurveyConfiguration[] = [
    {
      surveyId: '3Y6A066rNaDrV17TDvQBN1xYmgwm4o8Y47HwgTnPmkI=',
      conditions: [
        {name: 'viewed page Home', compare: 'GREATER_EQUAL', amount: 8},
        {name: 'clicked on play button', compare: 'GREATER_EQUAL', amount: 12},
      ],
      triggerEvent: 'viewed page Home',
    },
  ];


  private isWaitingSubscriptionModalDisplayed: boolean = false;

  get isMidiActive() {
    return midiDispatcher.state.isActive;
  }

  /* Lifecycle */

  get showPlugPianoSlide(): boolean {
    return playerModals.state.activeModals.includes('plugPianoSlide');
  }


  created() {
    this.loadUser();
    this.ensureContextIsGood();

    if (this.$q.platform.is.ios) {
      audioPlayer.watchContextInterruption(Tone.getContext().rawContext as AudioContext);
      audioPlayer.requestContextRebuild = () => this.rebuildAudioContext();
    }

    if (isInMobileApp() && this.$q.platform.is.capacitor) {
      this.setupMobile();
    }

    window.addEventListener('beforeunload', function () {
      timeTrackingController.checkLastActivity();
    });
  }

  /* Global Handlers */
  async mounted() {
    this.setupEvents();

    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.redirectToStorePageIfNeeded();

    // The initial setupMobile() call in created() can fire before the
    // Capacitor bridge has fully settled on Android (e.g. while the
    // splash screen is still transitioning).  Re-applying after mount
    // with a short delay guarantees the status bar is visible on the
    // homepage without waiting for the first route change.
    if (isInMobileApp() && this.$q.platform.is.capacitor) {
      setTimeout(() => this.setStatusBarStyleForCurrentPage(), 400);
    }

    // Bypass mute button/status of IOS
    if (isInIOSApp() && this.$q.platform.is.capacitor) {
      navigator.audioSession.type = 'play-and-record';
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new MediaMetadata({
        title: 'Tokatab',
      });
    }

    // crashlytics
    if (isInMobileApp()) {
      this.MobileMonitoring.sendUnsentReports();
      this.MobileMonitoring.setEnabled();
    }
  }

  beforeDestroy() {
    this.removeEvents();

    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    timeTrackingController.checkLastActivity();
  }

  async onVisibilityChange() {
    if (!document.hidden) {
      this.ensureContextIsGood();
      if (isInMobileApp()) {
        // @ts-ignore
        this.Capgo.updateMobileAppIfNeeded();
      }
      if (this.$q.platform.is.ios) {
        try {
          // Don't rely solely on a fresh .state read here — a context that lived through a real
          // interruption can already be self-reporting 'running' again by now even though it
          // never actually reconnected (see rebuildAudioContext()'s comment below). The flag
          // records what the live statechange listener actually witnessed, which .state can't be
          // trusted to still reflect at this point.
          if (audioPlayer.consumeContextInterruptionFlag() || Tone.getContext().state !== 'running') {
            await this.rebuildAudioContext();
          }
        }
        catch (error) {
          audioPlayer.triggerError(error, 'onVisibilityChange()');
        }
      }

      if(this.$q.platform.is.mobile && this.$q.platform.is.capacitor && !userService.user.configurations.onboardingConfig.hasFinishedFirstOnboarding) {
        this.loadUser();
      }
    }
    else {
      audioPlayer.emit('pause');
    }
  }

  // WebKit (iOS Safari/WKWebView) is known to leave an AudioContext in a "zombie" state after it
  // lives through a genuine audio-session interruption (another app — YouTube, Spotify, a phone
  // call, etc. — taking audio focus): resume() can resolve and .state can even flip back to
  // 'running' while the underlying native audio unit never actually reconnects, silently killing
  // playback moments later. A plain backgrounding (no interruption) is usually fine to just
  // resume, but .state alone can't reliably tell the two apart (both commonly report
  // 'suspended'), so we always rebuild from scratch instead of trusting resume() — the same
  // workaround used industry-wide for this behaviour.
  //
  // The actual resume() for the brand new context is deliberately left for the next real Play tap
  // (see AudioPlayer.play()): iOS only reliably unlocks audio output inside a genuine, synchronous
  // user-gesture handler, not inside this async visibilitychange callback — constructing a new
  // AudioContext needs no gesture, only resuming it out of its initial 'suspended' state does.
  async rebuildAudioContext() {
    const toneCtx = Tone.getContext();
    audioPlayer.state.isLoading = true;
    await toneCtx.close().catch(() => {/* already closed/unusable */});

    const newContext = new AudioContext();
    Tone.setContext(newContext, true);
    audioPlayer.watchContextInterruption(newContext);

    await synthController.initSynth(true);
    this.$root.$emit('reloadPlayableContent');
  }

  loadUser() {
    if (localStorage.tokenTokatab) {
      this.loaded = false;
      userService.fetchUserData().finally(() => {
        this.loaded = true;
      });
    }
    else {
      this.loaded = true;
    }
  }

  ensureContextIsGood() {
    if (!BackEndService.redirectToOfflinePageDependingOnConnexionStatus()) {
      BackEndService.redirectToMaintenancePageIfServerIsDown();
    }
    if (isInOldCordovaApp()) {
      this.$router.push('/update?majorUpdate=true');
    }
  }

  /* Mobile Handlers */

  setupEvents() {
    this.$root.$on('catalogModal_open', ({catalogEntry, allowPreview}) => {
      (this.$refs.catalogPopup as CatalogEntryPopup).show(catalogEntry, allowPreview);
    });
    this.$root.$on('forceCurrentPageOrientation', (forceOverride = false) => {
      this.forcePageOrientationIfNeeded(forceOverride);
    });
  }

  removeEvents() {
    this.$root.$off('catalogModal_open');
    this.$root.$off('displayWaitingSubscriptionModal');
    this.$root.$off('forceCurrentPageOrientation');
  }

  async setupMobile() {
    this.forcePageOrientationIfNeeded();
    this.setupUniversalLinksListener();
    this.setStatusBarStyleForCurrentPage();
    const capgoChannel = process.env.CAPGO_CHANNEL ?? 'prod';
    if(capgoChannel !== 'prod' && capgoChannel !== 'local') {
      // @ts-ignore
      await this.Capgo.setCapgoChannel(capgoChannel);
    }

    // @ts-ignore
    await this.Capgo.updateMobileAppIfNeeded();
    this.$root.$on('displayWaitingSubscriptionModal', () => {
      setTimeout(() => {
        this.isWaitingSubscriptionModalDisplayed = true;
      }, 2000);
    });
  }

  setupUniversalLinksListener() {
    // @ts-ignore
    this.MobileApp.addListener('appUrlOpen', (data: any) => {
      analyticsController.logBasicAnalyticsEvent('user opened app from universal link', 'Router', {url: data.url});
      if (data.url.includes('/#/')) {
        const resource = data.url.split('/#/').pop();
        this.$router.push(`/${resource}`);
      }
      else {
        this.$router.push('/home');
      }
    });
  }

  setStatusBarStyleForCurrentPage() {
    if (!isInMobileApp()) {
      return;
    }

    // @ts-ignore
    this.MobileScreen.setStatusBarStyle('white');
    // @ts-ignore
    this.MobileScreen.setNavigationBarColor('white');
  }

  @Watch('$route')
  redirectToStorePageIfNeeded() {
    if (this.$route.path === '/store') {
      return false;
    }
    if (!this.$q.platform.is.mobile && !this.$q.platform.is.ipad) {
      return false;
    }
    if (this.$route.query.forceWeb || localStorage.getItem('forceWeb') || isInMobileApp() || isInOldCordovaApp()) {
      return false;
    }
    if (isOutOfAppAuthorizedPage(this.$route.path)) {
      return false;
    }

    if (
      (this.$route.path === '/home' || this.$route.path === '/') &&
      !userService.user.configurations.onboardingConfig.hasFinishedFirstOnboarding
    ) {
      return false;
    }

    this.$router.push('/store');
    return true; // Force web on mobile -- audio testing
  }

  @Watch('isMidiActive')
  launchKeyboardExplanation() {
    if (
      this.isMidiActive &&
      userService.user.id !== 0 &&
      !userService.user.configurations.onboardingConfig.checklistTasksCompleted.includes('plug_piano') &&
      this.$route.name !== 'OnboardingRecap' &&
      userService.user.configurations.onboardingConfig.hasFinishedFirstOnboarding
    ) {
      // onboardingChecklistController.addPlugPianoMission();
      setTimeout(() => {
        if (!playerModals.state.activeModals.includes('keyboardSlide')) {
          playerModals.setIsModalDisplayed('plugPianoSlide', true);
        }
      }, 1200);
    }
  }

  completePlugPiano() {
    onboardingChecklistController.addPlugPianoMission();
    onboardingChecklistController.completeTask('plug_piano', true);
    this.hidePlugPianoSlide();
  }

  hidePlugPianoSlide() {
    playerModals.setIsModalDisplayed('plugPianoSlide', false);
  }

  @Watch('$route')
  private onRouteChangeForOrientation() {
    this.forcePageOrientationIfNeeded();
  }

  forcePageOrientationIfNeeded(forceOverride = false) {
    // Used mostly for safe area in desktop
    if (isLandscapePage(this.$route.path)) {
      document.getElementsByTagName('body')[0].classList.add('force-landscape');
      document.getElementsByTagName('body')[0].classList.remove('force-portrait');
    }
    else {
      document.getElementsByTagName('body')[0].classList.add('force-portrait');
      document.getElementsByTagName('body')[0].classList.remove('force-landscape');
    }

    if (this.$q.platform.is.ipad || !this.$q.platform.is.mobile || this.$q.screen.gt.sm) {
      return;
    }

    if (isInMobileApp() && isLandscapePage(this.$route.path)) {
      // @ts-ignore
      this.MobileScreen.lockScreenLandscape();
    }
    else if (isInMobileApp()) {
      // @ts-ignore
      this.MobileScreen.lockScreenPortrait();
    }
  }

  forceExerciseLandscapeLock() {
    document.getElementsByTagName('body')[0].classList.add('force-landscape');
    document.getElementsByTagName('body')[0].classList.remove('force-portrait');

    if (this.$q.platform.is.ipad || !this.$q.platform.is.mobile || this.$q.screen.gt.sm) {
      return;
    }

    if (isInMobileApp()) {
      // @ts-ignore
      this.MobileScreen.lockScreenLandscape();
    }
  }

  @Watch('$route')
  private updateStatusBarOnRouteChange() {
    // Player pages manage their own bar visibility (enterFullScreenMode /
    // leaveFullScreenMode). Calling setStatusBarStyleForCurrentPage() here
    // would race with PlayerPage.mounted()'s StatusBar.hide() and win because
    // our async StatusBar.show() resolves after the synchronous hide().
    if (isLandscapePage(this.$route.path)) return;
    this.setStatusBarStyleForCurrentPage();
  }

}
